/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Observable } from "@vienna-hypertext/observable";

import { PublicUser, PublicUserWithFollows } from "@vienna/common/src";
import { User as PrismaUser } from "@vienna/prisma";

import { stringToHSL } from "../../lib/utils/color";
import { API } from "../api";

import { BaseUserSnapshot, RequiredUserMethods } from "./user.interfaces";
import { UserConverter } from "./UserConverter";

export class BaseUser extends Observable<BaseUserSnapshot> implements RequiredUserMethods {
  private static avatarDomain = `https://vienna-${
    process.env.ENVIRONMENT === "prod" ? "prod" : "dev"
  }-avatar.imgix.net`;

  constructor(snapshot: BaseUserSnapshot) {
    super(snapshot);
  }

  /* -------------------------------------------------- */
  /*                       Plates                       */
  /* -------------------------------------------------- */
  public async getPlates(): Promise<void> {
    const plates = await API.users.getPlatesByUsername(this.snapshot.username);
    this.setState({ plates: [...plates] });
  }

  /* -------------------------------------------------- */
  /*              Link, Color, Username                 */
  /* -------------------------------------------------- */
  public get inAppLink(): string {
    return `/user/${this.snapshot.username}`;
  }

  public get color(): string {
    return stringToHSL(this.snapshot.username, { lit: [70, 80] });
  }

  public get prefixedUsername(): string {
    return `${this.snapshot.registeredWithUrbit ? "" : "@"}${this.snapshot.username}`;
  }

  public get readableDateJoined(): string {
    const date = new Date(this.snapshot.createdAt);
    return `Joined ${date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })}`;
  }

  public avatarURL(size: number): string {
    const isGif = this.snapshot.avatarName.endsWith(".gif");
    return `${BaseUser.avatarDomain}/${this.snapshot.avatarName}${
      isGif ? "" : `?fit=max&h=${size * 2}&w=${size * 2}`
    }`;
  }

  /* -------------------------------------------------- */
  /*                      Create                        */
  /* -------------------------------------------------- */
  public static fromBackendUser(user: PrismaUser | PublicUser | PublicUserWithFollows): BaseUser {
    const snapshot = UserConverter.toBaseUserSnapshot(user);
    return BaseUser.fromSnapshot(snapshot);
  }

  private static fromSnapshot(snapshot: BaseUserSnapshot): BaseUser {
    return new BaseUser(snapshot);
  }
}
